import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrayerForm from "@/components/PrayerForm";
import SEOHead from "@/components/SEOHead";

const PROGRAMS = [
  {
    num: "01",
    name: "The Workbook",
    tagline: "Begin on your own.",
    price: "$27",
    path: "/programs/workbook",
    accent: "bg-white",
    text: "text-[#380C1E]",
    sub: "text-[#380C1E]/70",
    border: "border-[#E8DEC8]",
    description:
      "From Burnout to Becoming is a guided purpose workbook for the mom who feels stretched thin. In honest, written pages you will uncover which of the six burnout sources is draining you, reclaim your identity, and plan your becoming one page at a time.",
    features: [
      "Self-paced, Scripture-rooted reflection",
      "Identify your six B's burnout source",
      "A written plan for what comes next",
    ],
    cta: "Get the workbook",
  },
  {
    num: "02",
    name: "Identity Reset",
    tagline: "Renew your thinking.",
    price: "$47",
    path: "/programs/identity-reset",
    accent: "bg-[#9E1B50]",
    text: "text-white",
    sub: "text-white/90",
    border: "border-white/10",
    description:
      "A workshop and interactive experience that helps you name the beliefs, labels, and survival patterns quietly shaping your choices, then replace them with truth and practical action. The guided introduction to the work on the page.",
    features: [
      "Teaching you can return to anytime",
      "Name the beliefs underneath your choices",
      "Bundle with the workbook and save",
    ],
    cta: "Reset your identity",
  },
  {
    num: "03",
    name: "Becoming Intensive",
    tagline: "Transform with support.",
    price: "$997",
    path: "/programs/becoming-intensive",
    accent: "bg-[#4A0E26]",
    text: "text-white",
    sub: "text-white/90",
    border: "border-white/10",
    description:
      "Six weeks live with Ieshia: Scripture-rooted teaching, coaching, community, and the whole framework worked out loud with a small cohort of women walking it with you. The full guided walk across the six B's.",
    features: [
      "Six weeks of live coaching and reflection",
      "A clear plan across your six B's",
      "Community, structure, and accountability",
    ],
    cta: "Join the Intensive",
  },
];

const Programs = () => {
  return (
    <>
      <SEOHead
        title="Stewardship & Transformational Programs: Ieshia Danielle"
        description="Explore the Becoming Pathway: From Burnout to Becoming Workbook, Identity Reset Workshop, and the 6-week Becoming Intensive mentorship experience."
        canonical="https://iamieshiadanielle.com/programs"
        ogImage="https://vibe.filesafe.space/1787853701390843161/assets/a5d05b54-0df1-474c-a727-ad33093a5f8e.png"
        schemaJson={[
          {
            "@type": "WebPage",
            "@id": "https://iamieshiadanielle.com/programs#webpage",
            url: "https://iamieshiadanielle.com/programs",
            name: "Stewardship & Transformational Programs: Ieshia Danielle",
            isPartOf: { "@id": "https://iamieshiadanielle.com/#website" },
            about: { "@id": "https://iamieshiadanielle.com/#person" },
            inLanguage: "en-US",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://iamieshiadanielle.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Programs",
                item: "https://iamieshiadanielle.com/programs",
              },
            ],
          },
        ]}
      />
      <div className="min-h-screen bg-[#FAF6F0] text-[#380C1E] flex flex-col font-sans">
        <Header />

        <main className="flex-1">
          {/* Hero */}
          <section className="bg-[#FBF1F4] py-16 md:py-24 border-b border-[#E8DEC8]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  The Becoming Pathway
                </p>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-[#380C1E] leading-[1.1]">
                  Three ways to move from burnout to{" "}
                  <span className="italic font-light text-[#9E1B50]">
                    becoming.
                  </span>
                </h1>
                <p className="text-base md:text-lg text-[#380C1E]/80 font-light leading-relaxed max-w-2xl">
                  Burnout did not happen in a day, and becoming will not either.
                  Each program leads into the next, so you can start where you
                  are and walk the path at your own pace.
                </p>
                <div className="flex flex-wrap gap-4 pt-2">
                  <a
                    href="#pathway"
                    className="bg-[#4A0E26] hover:bg-[#330A1F] text-white font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full transition-all shadow-sm"
                  >
                    Explore the pathway →
                  </a>
                  <a
                    href="https://quiz.iamieshiadanielle.com/quiz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-transparent border border-[#4A0E26] hover:bg-[#4A0E26] hover:text-white text-[#4A0E26] font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full transition-all"
                  >
                    Take The Quiz →
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-200 shadow-md border border-[#E8DEC8] aspect-[4/6]">
                  <img
                    src="https://drive.google.com/thumbnail?id=1jEH3uACTSLUh2fuALIm49VKyifqGgCbi&sz=w1800"
                    alt="Ieshia Danielle smiling while holding a Bible"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Truth strip */}
          <section className="bg-[#4A0E26] text-white py-10 px-6">
            <div className="max-w-5xl mx-auto text-center">
              <p className="font-serif text-xl md:text-2xl font-normal italic leading-snug">
                "You are strong. But strength has become the reason nobody
                notices you are disappearing."
              </p>
            </div>
          </section>

          {/* The Pathway */}
          <section id="pathway" className="bg-white py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
              <div className="max-w-3xl space-y-3">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  Start where you are
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
                  Each step builds on the last.
                </h2>
                <p className="text-[#380C1E]/70 font-light leading-relaxed">
                  Begin with the workbook to name what is draining you. Add
                  Identity Reset to renew your thinking. Then walk it all out
                  with support in the Becoming Intensive.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {PROGRAMS.map((p) => (
                  <div
                    key={p.num}
                    className={`${p.accent} ${p.text} rounded-3xl p-8 flex flex-col justify-between border ${p.border} shadow-sm relative`}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b border-current/20 pb-4">
                        <span className="text-xs font-bold opacity-60">
                          {p.num}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest font-semibold opacity-80">
                          {p.tagline}
                        </span>
                      </div>
                      <h3 className="font-serif text-2xl font-normal">
                        {p.name}
                      </h3>
                      <p
                        className={`text-sm leading-relaxed font-light ${p.sub}`}
                      >
                        {p.description}
                      </p>
                      <ul className="space-y-2 pt-1">
                        {p.features.map((f) => (
                          <li
                            key={f}
                            className={`flex items-start gap-2 text-xs sm:text-sm font-light ${p.sub}`}
                          >
                            <span className="mt-0.5">✓</span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pt-8 space-y-4">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="font-serif text-3xl font-semibold">
                          {p.price}
                        </span>
                      </div>
                      <Link
                        to={p.path}
                        className={`inline-block font-bold uppercase text-[11px] tracking-wider ${
                          p.accent === "bg-white"
                            ? "text-[#9E1B50] hover:underline"
                            : "text-white hover:underline"
                        }`}
                      >
                        {p.cta} →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* The Six B's overview */}
          <section className="bg-[#FBF1F4] py-16 md:py-24 border-y border-[#E8DEC8]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
              <div className="max-w-3xl space-y-3">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  The complete pattern
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
                  Burnout has a source. We are going to find yours.
                </h2>
                <p className="text-[#380C1E]/70 font-light leading-relaxed">
                  The six B's give us a practical way to see the beliefs,
                  relationships, responsibilities, health, work, and money
                  patterns shaping your life.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { num: "01", title: "Beliefs" },
                  { num: "02", title: "Belonging" },
                  { num: "03", title: "Boundaries" },
                  { num: "04", title: "Business" },
                  { num: "05", title: "Budgeting" },
                  { num: "06", title: "Body" },
                ].map((b) => (
                  <div
                    key={b.num}
                    className="bg-[#EFE6D2] rounded-2xl p-8 space-y-6 border border-[#E8DEC8]"
                  >
                    <span className="text-xs font-semibold text-[#380C1E]/50 tracking-wider">
                      {b.num}
                    </span>
                    <h3 className="font-serif text-2xl font-normal text-[#380C1E]">
                      {b.title}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="bg-[#9E1B50] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
              <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-white/80">
                Your next step
              </p>
              <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl font-normal leading-[1.15] text-white max-w-4xl">
                You do not need another thing to carry. You need a path.
              </h2>
              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/programs/becoming-intensive"
                  className="bg-white hover:bg-white/90 text-[#9E1B50] font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full shadow-sm inline-block transition-all"
                >
                  Explore the pathway →
                </Link>
                <Link
                  to="/book"
                  className="bg-transparent border border-white text-white hover:bg-white hover:text-[#9E1B50] font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full transition-all"
                >
                  Book Ieshia →
                </Link>
              </div>
            </div>
          </section>

          <PrayerForm />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Programs;
